import fs from 'node:fs';
import path from 'node:path';

const DEFAULT_MAX_LOG_BYTES = 50 * 1024 * 1024;
const DEFAULT_RETENTION_DAYS = 30;

export interface DailyLogRotatorOptions {
  maxBytes?: number;
  retentionDays?: number;
  now?: () => Date;
}

/** The UTC daily rotation key used in log archive filenames. */
export function periodKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Active file `<dir>/desktop.log.txt` -> archive-name stem `desktop`. */
function activePrefix(file: string): string {
  const base = path.basename(file);
  return base.endsWith('.log.txt') ? base.slice(0, -'.log.txt'.length) : base.replace(/\.txt$/, '');
}

/** A free archive path for a period — `desktop.2026-06-23.txt`, or
 *  `desktop.2026-06-23.1.txt` if a same-period archive exists. */
function archivePath(file: string, period: string): string {
  const dir = path.dirname(file);
  const prefix = activePrefix(file);
  let candidate = path.join(dir, `${prefix}.${period}.txt`);
  for (let n = 1; fs.existsSync(candidate); n++) {
    candidate = path.join(dir, `${prefix}.${period}.${n}.txt`);
  }
  return candidate;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function cutoffPeriod(now: Date, retentionDays: number): string {
  const cutoff = new Date(now.getTime());
  cutoff.setUTCDate(cutoff.getUTCDate() - retentionDays);
  return periodKey(cutoff);
}

/**
 * Delete archives whose filenames prove they are older than the retention
 * window. This intentionally ignores mtime and ignores non-dated filenames:
 * only `prefix.YYYY-MM-DD(.n).txt` files are eligible for deletion.
 */
function pruneDatedArchives(file: string, now: Date, retentionDays: number): void {
  const dir = path.dirname(file);
  const prefix = activePrefix(file);
  const archiveNameRe = new RegExp(`^${escapeRegExp(prefix)}\\.(\\d{4}-\\d{2}-\\d{2})(?:\\.\\d+)?\\.txt$`);
  const cutoff = cutoffPeriod(now, retentionDays);
  let names: string[];

  try {
    names = fs.readdirSync(dir);
  } catch {
    return;
  }

  for (const name of names) {
    const match = archiveNameRe.exec(name);
    if (!match || match[1] >= cutoff) continue;
    try {
      fs.rmSync(path.join(dir, name), { force: true });
    } catch {
      // Best-effort.
    }
  }
}

/**
 * Rotates a live log file into dated UTC-day archives and prunes archives whose
 * filenames indicate they are older than the retention window.
 */
export class DailyLogRotator {
  private liveFile: string | null = null;
  private liveLogBytes: number | null = null;
  private currentPeriod: string | null = null;
  private readonly maxBytes: number;
  private readonly retentionDays: number;
  private readonly now: () => Date;

  constructor(options: DailyLogRotatorOptions = {}) {
    this.maxBytes = options.maxBytes ?? DEFAULT_MAX_LOG_BYTES;
    this.retentionDays = options.retentionDays ?? DEFAULT_RETENTION_DAYS;
    this.now = options.now ?? (() => new Date());
  }

  rotateIfNeeded(file: string, incomingBytes: number): void {
    if (this.liveFile !== file) {
      this.liveFile = file;
      this.liveLogBytes = null;
      this.currentPeriod = null;
    }

    if (this.currentPeriod === null) {
      try {
        const stats = fs.statSync(file);
        this.liveLogBytes = stats.size;
        this.currentPeriod = periodKey(stats.mtime);
      } catch {
        this.liveLogBytes = 0;
        this.currentPeriod = periodKey(this.now());
      }
    }

    const now = this.now();
    const nowPeriod = periodKey(now);
    const periodChanged = this.currentPeriod !== nowPeriod;
    const sizeExceeded = (this.liveLogBytes ?? 0) + incomingBytes > this.maxBytes;

    // Only ever rotate a file that has content — never archive an empty one.
    if ((periodChanged || sizeExceeded) && (this.liveLogBytes ?? 0) > 0) {
      try {
        fs.renameSync(file, archivePath(file, this.currentPeriod ?? nowPeriod));
        pruneDatedArchives(file, now, this.retentionDays);
        this.liveLogBytes = 0;
        this.currentPeriod = nowPeriod;
        return;
      } catch {
        // Couldn't rename (transient FS error); re-sync the byte counter to the
        // real size so the cap still holds, and leave currentPeriod so the next
        // write retries.
        try {
          this.liveLogBytes = fs.statSync(file).size;
        } catch {
          this.liveLogBytes = 0;
        }
        return;
      }
    }

    this.currentPeriod = nowPeriod;
  }

  recordWrite(bytes: number): void {
    this.liveLogBytes = (this.liveLogBytes ?? 0) + bytes;
  }
}

export function appendRotatingLogSync(file: string, line: string, rotator: DailyLogRotator): void {
  const bytes = Buffer.byteLength(line);
  rotator.rotateIfNeeded(file, bytes);
  fs.appendFileSync(file, line);
  rotator.recordWrite(bytes);
}

export interface RotatingLogStreamOptions extends DailyLogRotatorOptions {
  alsoWriteTo?: { write(message: string): void };
}

export function createRotatingLogStream(file: string, options: RotatingLogStreamOptions = {}): { write(message: string): void } {
  const rotator = new DailyLogRotator(options);
  return {
    write(message: string): void {
      try {
        fs.mkdirSync(path.dirname(file), { recursive: true, mode: 0o700 });
        appendRotatingLogSync(file, message, rotator);
      } catch {
        // Logging must never change application behavior.
      }

      try {
        options.alsoWriteTo?.write(message);
      } catch {
        // Logging must never change application behavior.
      }
    },
  };
}
